/// <amd-module name="Wit/WorkItemGitLinks/Components/Commit/CommitForm" />
import * as React from "react";
import { useRef } from "react";

import { format } from "VSS/Core/Util/String";
import { useDebouncedSubscription, useDerivedObservable, useObservable } from "VSS/Core/Observable";
import { usePageContext } from "VSS/Platform/Layout";
import { TextField } from "VSSUI/TextField";
import { Button } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";

import { getGitClient, IGitRestClient } from "VC/Common/RestClient/Git";
import { GitCommitRef, GitQueryCommitsCriteria, GitRepository } from "VC/Common/Contracts/Git";

import * as Resources from "Wit/WorkItemGitLinks/Resources";
import { ILinkTypeSpecificFormProps } from "../ILinkTypeSpecificFormProps";
import { commitToDisplayData } from "Wit/WorkItemGitLinks/GitLinkUtils";
import { CommitPickerDialog } from "./CommitPickerDialog";

const shaLength = 40;
const partialSHAPattern = /^[0-9A-Fa-f]{1,40}$/;

export function CommitForm({ onArtifactsUpdated, repository }: ILinkTypeSpecificFormProps) {
    const [commitPickerDialogOpen, setCommitPickerDialogOpen] = useObservable<boolean>(false);
    const [commitSHA, setCommitSHA] = useObservable<string>("");

    const [errorMessage, setErrorMessage] = useObservable<string | undefined>(undefined);
    const error = useDerivedObservable(errorMessage, message => !!message, []);

    const pageContext = usePageContext();
    const gitClient = useRef<IGitRestClient>(getGitClient(pageContext));

    useDebouncedSubscription(
        commitSHA,
        300,
        newCommitSHA => {
            if (!repository) {
                return; // Don't search yet
            }

            getCommitByPartialSHA(gitClient.current, repository, newCommitSHA)
                .then(commit => {
                    if (newCommitSHA !== commitSHA.value || !repository) {
                        // The input has been changed since this request fired; do noting with the response.
                        return;
                    }
                    const artifact = commitToDisplayData(pageContext, commit, repository);
                    onArtifactsUpdated([artifact]);
                    setErrorMessage(undefined);
                })
                .catch((e: unknown) => {
                    onArtifactsUpdated([]);
                    setErrorMessage(e instanceof Error ? e.message : `${e}`);
                });
        },
        [repository]
    );

    return (
        <>
            <FormItem className="commit-form-item" label={Resources.CommitIDFieldLabel} error={error} message={errorMessage}>
                <div className="flex-row flex-wrap gap-8">
                    <TextField
                        className="monospaced-m"
                        containerClassName="flex-grow"
                        disabled={!repository}
                        value={commitSHA}
                        onChange={(e, val) => setCommitSHA(val)}
                    />
                    <Button text={Resources.FindCommitsButtonLabel} disabled={!repository} onClick={() => setCommitPickerDialogOpen(true)} />
                </div>
            </FormItem>

            <ConditionalChildren renderChildren={commitPickerDialogOpen}>
                <CommitPickerDialog
                    repository={repository!}
                    onCommitChosen={newCommitSHA => {
                        commitSHA.value = newCommitSHA;
                        setCommitPickerDialogOpen(false);
                    }}
                    onDismiss={() => setCommitPickerDialogOpen(false)}
                />
            </ConditionalChildren>
        </>
    );
}

async function getCommitByPartialSHA(gitClient: IGitRestClient, repository: GitRepository, partialSHA: string): Promise<GitCommitRef> {
    if (!partialSHA) {
        throw new Error(Resources.MissingCommitIDError);
    }

    if (!partialSHAPattern.test(partialSHA)) {
        throw new Error(Resources.InvalidCommitIDError);
    }

    const minSHA = partialSHA.padEnd(shaLength, "0");
    const maxSHA = partialSHA.padEnd(shaLength, "f");
    const matchingCommits = await gitClient.getCommits(
        repository.id,
        { fromCommitId: minSHA, toCommitId: maxSHA } as GitQueryCommitsCriteria,
        repository.project.id
    );

    if (matchingCommits.length === 0) {
        throw new Error(format(Resources.NoMatchingCommitError, partialSHA));
    }

    if (matchingCommits.length > 1) {
        throw new Error(format(Resources.MulipleMatchingCommitsError, partialSHA));
    }

    return matchingCommits[0];
}
